import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  type QuestionnaireAnswers,
  type RenderableSection,
  type SectionErrors,
  toRenderableField,
  totalUploadBytes,
  validateSection,
} from '@/features/questionnaires/elements/answers';
import { QuestionnaireRenderer } from '@/features/questionnaires/elements/QuestionnaireRenderer';
import { toast } from '@/lib/notify';

import { useSessionQuestionnaire } from '../../api/useSessionQuestionnaire';
import { useVerifyStep } from '../../api/useVerifyStep';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import type { QuestionnaireStepInput } from './questionnaire.utils';

/**
 * The applicant's view of a Questionnaire step.
 *
 * ONE SECTION PER SCREEN. A questionnaire can hold many sections, and putting them all on one
 * scrolling page would present a phone user with a wall of inputs; the builder's own preview pages
 * through them the same way, so what an admin designed is what an applicant walks.
 *
 * The form itself is `QuestionnaireRenderer` — the exact component the builder previews with. That
 * is the whole point of Phase 3: there is no second implementation of a date picker or a dropdown
 * cascade to drift from the first.
 *
 * NOTHING IS SUBMITTED UNTIL THE LAST SECTION. Answers accumulate locally and go up in one call, so
 * a half-answered questionnaire never lands in the database looking like a completed step. The cost
 * is that closing the tab mid-way loses the answers — accepted deliberately: a per-section save
 * would need a "partially answered" step state that the session status model has no room for, and
 * would make an abandoned run indistinguishable from a finished one.
 */
export function QuestionnaireStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { data, isLoading, error } = useSessionQuestionnaire(session.id, step.id, true);
  const { mutate, isPending } = useVerifyStep(session.id);

  const [sectionIndex, setSectionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [errors, setErrors] = useState<SectionErrors>({});
  const [expired, setExpired] = useState(false);

  // Sections with no fields are dropped. An admin can leave one empty while building, and asking
  // an applicant to press Continue on a screen with nothing on it wastes a tap and reads as a bug.
  const sections = useMemo<RenderableSection[]>(() => {
    if (!data) return [];
    return data.sections
      .filter((s) => s.fields.length > 0)
      .map((s) => ({
        // The section id is the answer key, so it must survive into the renderable shape.
        id: s.id,
        title: s.title?.trim() || s.name,
        description: s.description ?? '',
        fields: s.fields.map(toRenderableField),
      }));
  }, [data]);

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <Loader2 size={22} className="animate-spin text-primary" />
        <p className="text-[length:var(--text-14)] text-[#666666]">Loading your questions…</p>
      </div>
    );
  }

  // Either the questionnaire was deleted after it was attached, or the step has none. The applicant
  // cannot act on this and must not be given a Continue button that submits nothing, so it is a
  // dead end that points them at the organisation — deliberately NOT the provider-style wording,
  // because nothing failed technically.
  if (error || sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FDECEC]">
          <AlertTriangle size={24} className="text-destructive" />
        </div>
        <h3 className="text-base font-semibold text-[#041A3A]">Questions unavailable</h3>
        <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
          There's nothing to answer here yet. Please contact the organisation that sent you this
          link.
        </p>
      </div>
    );
  }

  const index = Math.min(sectionIndex, sections.length - 1);
  const section = sections[index];
  if (!section) return null;

  const sectionAnswers = answers[section.id] ?? {};
  const isLast = index === sections.length - 1;

  function handleChange(fieldId: string, value: unknown) {
    if (!section) return;
    setAnswers((prev) => ({
      ...prev,
      [section.id]: { ...(prev[section.id] ?? {}), [fieldId]: value },
    }));
    // Clear this field's error as soon as it is touched. Leaving it until the next Continue means
    // an applicant fixes the field and still stares at the complaint about it.
    setErrors((prev) => {
      if (!(fieldId in prev)) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }

  function handleContinue() {
    if (!section) return;

    const found = validateSection(section, sectionAnswers);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setErrors({});

    if (!isLast) {
      setSectionIndex(index + 1);
      return;
    }

    // Last section — validate EVERY section before submitting, not just this one.
    //
    // Still necessary WITHOUT a Back button: this very loop can send the applicant backwards to fix
    // something, and from there they walk forward again through sections whose answers are already
    // in state. Re-checking all of them is what guarantees the submitted set is complete.
    for (let i = 0; i < sections.length; i++) {
      const candidate = sections[i];
      if (!candidate) continue;
      const sectionErrors = validateSection(candidate, answers[candidate.id] ?? {});
      if (Object.keys(sectionErrors).length > 0) {
        setSectionIndex(i);
        setErrors(sectionErrors);
        return;
      }
    }

    const input: QuestionnaireStepInput = { answers };
    mutate(
      { stepId: step.id, input: input as unknown as Record<string, unknown> },
      {
        onError: (err) => {
          if (isSessionExpiredError(err)) {
            setExpired(true);
            return;
          }
          toast.error("Couldn't save your answers. Please try again.");
        },
      },
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col items-center text-center">
        <QuestionnaireRenderer
          section={section}
          answers={sectionAnswers}
          onChange={handleChange}
          errors={errors}
          // Across EVERY section, not just this one: the whole step goes up in a single request, so
          // the upload budget is shared by all of it. Only this component can see them all.
          stepUploadBytes={totalUploadBytes(answers)}
        />
      </div>

      {/*
        NO "Section 1 of 2" COUNTER, AND NO BACK BUTTON — decided 2026-08-20, do not reinstate.

        The applicant's run is a forward-only flow: every other step (PAN, Aadhaar, Face, Bank) shows
        one screen and one Continue, and a questionnaire that alone offered navigation made this step
        look like a different product mid-run. The counter had the same problem — it exposed the
        step's internal structure, which is an authoring detail, and turned a short form into
        something that announces how much is left.

        The applicant can still be moved BACKWARDS automatically: `handleContinue` re-validates every
        section before submitting and jumps to the first one with a problem, which is the only case
        where going back is actually useful. That is deliberate and unaffected by removing the button.
      */}
      {/* One button, full width — the same single-CTA footer every other step runner has. */}
      <div className="mt-auto pt-4">
        <button
          type="button"
          onClick={handleContinue}
          disabled={isPending}
          style={{
            backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
            color: 'var(--brand-btn-text, #ffffff)',
            borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
            borderRadius: 'var(--brand-btn-radius, 0.75rem)',
          }}
          className="flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isPending && <Loader2 size={15} className="animate-spin" />}
          {/* ALWAYS "Continue", never "Submit".
              Every other step runner — PAN, Aadhaar, Bank, all of them — says Continue, and this
              step is one screen in a longer run. "Submit" said the run was finishing, which was
              actively wrong: a questionnaire is usually followed by more steps, so the applicant
              pressed Submit and got another screen. It was showing on EVERY screen too, because a
              questionnaire with one section makes the first section the last one. */}
          Continue
        </button>
      </div>
    </div>
  );
}
